import React, { useEffect, useRef, useState } from "react";
import {
  Trash2, Eye, EyeOff, Upload, Link2, AudioLines, Film, Image as ImageIcon,
  MapPin, ChevronLeft, X, House, Mountain, Users, FolderDown, Check, Folder, FolderPlus, ArrowUp, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { bustAssets } from "@/lib/assets";

const EMPTY_LINK = { title: "", description: "", media_type: "video", url: "", visible: true, folder: "" };
const EMPTY_UPLOAD = { file: null, title: "", description: "", visible: true, folder: "" };
const NO_FOLDER = "__none__";

// Sezioni della pagina in cui un contenuto può essere destinato, per area del sito.
const SECTIONS = {
  home: [
    { value: "home_hero", label: "Immagine di sfondo", types: ["image"] },
    { value: "home_film", label: "Il film (video di apertura)", types: ["video"] },
  ],
  tour: [
    { value: "tour_cover", label: "Foto principale", types: ["image"] },
    { value: "tour_gallery", label: "Gallery → Foto", types: ["image"] },
    { value: "tour_videos", label: "Gallery → Video", types: ["video"] },
    { value: "tour_audio", label: "Gallery → Audio", types: ["audio"] },
  ],
  leader: [
    { value: "leader_portrait", label: "Ritratto", types: ["image"] },
    { value: "leader_gallery", label: "Galleria e media → Foto", types: ["image"] },
    { value: "leader_videos", label: "Galleria e media → Video", types: ["video"] },
    { value: "leader_audio", label: "Galleria e media → Audio", types: ["audio"] },
  ],
};
const LEGACY_SECTIONS = [
  { value: "tour_stories", label: "Storie dal campo (vecchia sezione)" },
  { value: "leader_stories", label: "Storie e video (vecchia sezione)" },
];
const ALL_SECTIONS = [...SECTIONS.home, ...SECTIONS.tour, ...SECTIONS.leader, ...LEGACY_SECTIONS];
const sectionLabel = (v) => (ALL_SECTIONS.find((s) => s.value === v) || { label: "Solo libreria (non sul sito)" }).label;

const resolveUrl = (url) => (url.startsWith("/api/files/") ? fileUrl(url.replace("/api/files/", "")) : url);
const guessType = (name) => {
  if (/\.(mp3|wav|m4a|ogg|aac)$/i.test(name)) return "audio";
  if (/\.(mp4|mov|webm)$/i.test(name)) return "video";
  return "image";
};

const TYPE_TABS = [
  { value: "image", label: "Immagini", icon: ImageIcon },
  { value: "video", label: "Video", icon: Film },
  { value: "audio", label: "Audio", icon: AudioLines },
];
const EMPTY_MSG = {
  image: "Nessuna immagine in libreria: carica una foto o aggiungi un link qui sopra.",
  video: "Nessun video in libreria: carica un file o aggiungi un link qui sopra.",
  audio: "Nessun audio in libreria: carica una traccia o aggiungi un link qui sopra.",
};

function Thumb({ m }) {
  const [err, setErr] = useState(false);
  const src = resolveUrl(m.url);
  const FallbackIcon = m.media_type === "video" ? Film : ImageIcon;
  if (err || m.media_type === "audio") {
    return (
      <div className="h-44 flex flex-col items-center justify-center gap-2 bg-obsidian2 border-b border-white/5" data-testid={`cms-thumb-${m.id}`}>
        <FallbackIcon className="h-8 w-8 text-copper" />
        {m.media_type === "audio" && <span className="font-mono text-[10px] uppercase tracking-widest text-sand">Traccia audio</span>}
      </div>
    );
  }
  if (m.media_type === "video") {
    return <video src={src} preload="metadata" muted playsInline onError={() => setErr(true)} className="w-full h-44 object-cover bg-obsidian2 border-b border-white/5" data-testid={`cms-thumb-${m.id}`} />;
  }
  return <img src={src} alt={m.title} loading="lazy" onError={() => setErr(true)} className="w-full h-44 object-cover border-b border-white/5" data-testid={`cms-thumb-${m.id}`} />;
}

function DestinaWizard({ item, tours, leaders, onClose, onApply }) {
  const [area, setArea] = useState(null);
  const [slug, setSlug] = useState("");
  const [section, setSection] = useState("");
  const type = item.media_type;

  const areas = [
    { value: "home", label: "Home", desc: "La pagina iniziale del sito", icon: House, hidden: !SECTIONS.home.some((s) => s.types.includes(type)) },
    { value: "tour", label: "Spedizioni", desc: "Le pagine delle spedizioni", icon: Mountain, hidden: false },
    { value: "leader", label: "Tour Leader", desc: "Le pagine dei tour leader", icon: Users, hidden: false },
    { value: "none", label: "Solo libreria", desc: "Tieni il file senza pubblicarlo", icon: FolderDown, hidden: false },
  ].filter((a) => !a.hidden);

  const needsTarget = area === "tour" || area === "leader";
  const total = needsTarget ? 3 : 2;
  const step = !area ? 1 : needsTarget && !slug ? 2 : total;
  const areaLabel = { home: "Home", tour: "Spedizioni", leader: "Tour Leader", none: "Solo libreria" }[area] || "";
  const targetTitle = area === "tour" ? (tours.find((t) => t.slug === slug) || {}).title : (leaders.find((l) => l.slug === slug) || {}).name;
  const sectionOptions = (SECTIONS[area] || []).filter((s) => s.types.includes(type));
  const rowCls = "w-full flex items-center justify-between gap-3 bg-obsidian2 border p-4 text-left transition-colors hover:border-copper/60";
  const back = () => (step === 2 || area === "none" || area === "home" ? setArea(null) : setSlug(""));

  return (
    <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="cms-wizard-modal">
      <div className="max-w-lg mx-auto card-dark p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {item.media_type === "image" && <img src={resolveUrl(item.url)} alt="" className="h-14 w-24 object-cover border border-white/10 shrink-0" />}
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-copper">Destina nel sito · passo {step} di {total}</p>
              <h2 className="font-serif text-2xl text-cream mt-1 truncate">{item.title}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-sand hover:text-cream shrink-0" data-testid="cms-wiz-close"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 && (
          <>
            <p className="label mt-6">Dove vuoi destinarlo?</p>
            <div className="space-y-2">
              {areas.map((a) => (
                <button key={a.value} onClick={() => { setArea(a.value); setSlug(""); setSection(""); }} className={rowCls + " !justify-start"} data-testid={`cms-wiz-area-${a.value}`}>
                  <a.icon className="h-5 w-5 text-copper shrink-0" />
                  <span className="min-w-0"><span className="block text-cream text-sm">{a.label}</span><span className="block text-sand text-xs mt-0.5">{a.desc}</span></span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && needsTarget && (
          <>
            <p className="label mt-6">{area === "tour" ? "Quale spedizione?" : "Quale tour leader?"}</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(area === "tour" ? tours : leaders).map((t) => (
                <button key={t.id} onClick={() => { setSlug(t.slug); setSection(""); }} className={rowCls} data-testid={`cms-wiz-target-${t.slug}`}>
                  <span className="text-cream text-sm truncate">{area === "tour" ? t.title : t.name}</span>
                  {area === "tour" && t.location ? <span className="font-mono text-[10px] uppercase tracking-widest text-sand text-right shrink-0">{t.location}</span> : null}
                </button>
              ))}
            </div>
          </>
        )}

        {area === "none" && step === 2 && (
          <div className="mt-6 border border-white/10 bg-obsidian2 p-4" data-testid="cms-wiz-none-confirm">
            <p className="text-sm text-cream">Il contenuto resterà solo in libreria e non sarà visibile sul sito pubblico.</p>
          </div>
        )}

        {area && area !== "none" && (area === "home" || slug) && (
          <>
            <p className="label mt-6">Quale sezione della pagina?</p>
            <div className="space-y-2">
              {sectionOptions.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSection(s.value)}
                  className={(section === s.value ? "border-copper/70 bg-copper/10" : "border-white/10 bg-obsidian2 hover:border-copper/60") + " w-full flex items-center justify-between gap-3 border p-4 text-left transition-colors"}
                  data-testid={`cms-wiz-section-${s.value}`}
                >
                  <span className="text-cream text-sm">{s.label}</span>
                  {section === s.value && <Check className="h-4 w-4 text-copper shrink-0" />}
                </button>
              ))}
            </div>
          </>
        )}

        {step >= 2 && (
          <div className="mt-6 space-y-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-sand truncate" data-testid="cms-wiz-summary">
              {area === "none" ? "Solo libreria" : `${areaLabel}${targetTitle ? ` · ${targetTitle}` : ""}${section ? ` · ${sectionLabel(section)}` : ""}`}
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button onClick={back} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="cms-wiz-back"><ChevronLeft className="h-4 w-4" /> Indietro</button>
              {area === "none" ? (
                <button onClick={() => onApply(item, { area })} className="btn-ghost !px-5 !py-2.5 !text-xs !text-red-300 hover:!border-red-400" data-testid="cms-wiz-confirm-none">Rimuovi dal sito</button>
              ) : (
                <button onClick={() => onApply(item, { area, slug, section })} disabled={!section} className="btn-copper !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="cms-wiz-confirm">Destina qui</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [tours, setTours] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [tab, setTab] = useState("upload");
  const [typeTab, setTypeTab] = useState("image");
  const [wiz, setWiz] = useState(null);
  const [linkForm, setLinkForm] = useState({ ...EMPTY_LINK });
  const [uploadForm, setUploadForm] = useState({ ...EMPTY_UPLOAD });
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [folderFilter, setFolderFilter] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
    api.get("/leaders").then((r) => setLeaders(r.data)).catch(() => {});
  }, []);

  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (uploadForm.file && guessType(uploadForm.file.name) === "image") {
      const url = URL.createObjectURL(uploadForm.file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [uploadForm.file]);

  const dropFile = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) setUploadForm((f) => ({ ...f, file }));
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error("Scegli un file da caricare");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("title", uploadForm.title || uploadForm.file.name);
      fd.append("description", uploadForm.description);
      fd.append("media_type", guessType(uploadForm.file.name));
      fd.append("visible", String(uploadForm.visible));
      fd.append("folder", uploadForm.folder || "");
      const res = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
        },
      });
      toast.success("Contenuto caricato in libreria");
      setUploadForm({ ...EMPTY_UPLOAD });
      if (fileRef.current) fileRef.current.value = "";
      bustAssets();
      load();
      setWiz({ item: res.data });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const submitLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/media", { ...linkForm, source: "link" });
      toast.success("Link aggiunto in libreria");
      setLinkForm({ ...EMPTY_LINK });
      bustAssets();
      load();
      setWiz({ item: res.data });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (m) => {
    try {
      await api.patch(`/admin/media/${m.id}`, { visible: !m.visible });
      bustAssets();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Rimuovere "${m.title}" dalla libreria?`)) return;
    try {
      await api.delete(`/admin/media/${m.id}`);
      toast.success("Contenuto rimosso");
      bustAssets();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const applyWiz = async (item, choice) => {
    const patch =
      choice.area === "none" ? { placement: "", tour_slug: "", leader_slug: "" }
      : choice.area === "home" ? { placement: choice.section, tour_slug: "", leader_slug: "" }
      : choice.area === "tour" ? { placement: choice.section, tour_slug: choice.slug, leader_slug: "" }
      : { placement: choice.section, tour_slug: "", leader_slug: choice.slug };
    try {
      await api.patch(`/admin/media/${item.id}`, patch);
      toast.success("Posizione aggiornata");
      setWiz(null);
      bustAssets();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setFolder = async (m, folder) => {
    try {
      await api.patch(`/admin/media/${m.id}`, { folder });
      toast.success(folder ? `Spostato in "${folder}"` : "Rimosso dalla cartella");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const newFolder = (m) => {
    const name = window.prompt("Nome della nuova cartella (es. Tour Leader — Nome Cognome, Spedizione — Brasile 2027)");
    if (name && name.trim()) setFolder(m, name.trim());
  };

  const moveItem = async (m, direction) => {
    try {
      await api.post(`/admin/media/${m.id}/move`, { direction });
      bustAssets();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const folders = Array.from(new Set(media.map((m) => m.folder).filter(Boolean))).sort();
  const suggestedFolders = Array.from(new Set([
    ...folders,
    ...leaders.map((l) => `Tour Leader — ${l.name}`),
    ...tours.map((t) => `Spedizione — ${t.title}`),
  ]));
  const inFolder = (m) => (!folderFilter ? true : folderFilter === NO_FOLDER ? !m.folder : m.folder === folderFilter);
  const siblings = (m) => media.filter((x) => x.media_type === m.media_type && (x.placement || "") === (m.placement || "") && (x.tour_slug || "") === (m.tour_slug || "") && (x.leader_slug || "") === (m.leader_slug || ""));

  const targetName = (m) => {
    if ((m.placement || "").startsWith("tour_")) return (tours.find((t) => t.slug === m.tour_slug) || {}).title;
    if ((m.placement || "").startsWith("leader_")) return (leaders.find((l) => l.slug === m.leader_slug) || {}).name;
    return "";
  };

  const count = (t) => media.filter((m) => m.media_type === t && inFolder(m)).length;
  const shown = media.filter((m) => m.media_type === typeTab && inFolder(m));

  return (
    <div data-testid="admin-media">
      <h1 className="font-serif text-3xl sm:text-4xl">Media CMS</h1>
      <p className="text-cream2 text-sm mt-1">Carica file o aggiungi link: tutto finisce in libreria, poi la procedura guidata ti chiede dove pubblicarlo sul sito.</p>

      <div className="flex gap-2 mt-8" data-testid="cms-tabs">
        <button onClick={() => setTab("upload")} className={"font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border transition-colors " + (tab === "upload" ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid="cms-tab-upload">
          <Upload className="h-3.5 w-3.5 inline mr-2" /> Carica file
        </button>
        <button onClick={() => setTab("link")} className={"font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border transition-colors " + (tab === "link" ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid="cms-tab-link">
          <Link2 className="h-3.5 w-3.5 inline mr-2" /> Link esterno
        </button>
      </div>

      {tab === "upload" ? (
        <form onSubmit={submitUpload} className="card-dark p-6 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="cms-upload-form">
          <div className="sm:col-span-2">
            <label className="label">File (immagini, audio mp3/wav, video mp4/mov)</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={dropFile}
              className={"border border-dashed p-5 text-center transition-colors " + (dragOver ? "border-copper bg-copper/10" : "border-white/15 bg-obsidian2")}
              data-testid="cms-dropzone"
            >
              <Upload className={"h-6 w-6 mx-auto transition-colors " + (dragOver ? "text-copper" : "text-sand")} />
              <p className="text-sm text-cream2 mt-2">{dragOver ? "Rilascia qui il file" : "Trascina qui un file"}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand mt-2">oppure</p>
              <input ref={fileRef} type="file" accept="image/*,audio/*,video/*" className="field !w-full sm:!w-auto max-w-full !py-2 mx-auto mt-3 file:mr-4 file:px-3 file:py-1.5 file:border-0 file:bg-copper file:text-obsidian file:font-medium cursor-pointer" onChange={(e) => setUploadForm((f) => ({ ...f, file: e.target.files[0] }))} data-testid="cms-file-input" />
            </div>
            {uploadForm.file && <p className="font-mono text-[10px] text-sand mt-2 truncate" data-testid="cms-dropzone-filename">Selezionato: {uploadForm.file.name}</p>}
            {previewUrl && <img src={previewUrl} alt="Anteprima del file scelto" className="mt-3 w-full h-32 object-cover border border-white/10" data-testid="cms-upload-preview" />}
          </div>
          <div><label className="label">Titolo</label><input className="field" value={uploadForm.title} onChange={(e) => setUploadForm((f) => ({ ...f, title: e.target.value }))} placeholder="Uguale al nome file se vuoto" data-testid="cms-upload-title" /></div>
          <div><label className="label">Descrizione</label><input className="field" value={uploadForm.description} onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))} data-testid="cms-upload-desc" /></div>
          <div className="sm:col-span-2">
            <label className="label">Cartella (facoltativa)</label>
            <input className="field" list="cms-folder-suggestions" placeholder="Es. Tour Leader — Nome Cognome, Spedizione — Brasile 2027" value={uploadForm.folder} onChange={(e) => setUploadForm((f) => ({ ...f, folder: e.target.value }))} data-testid="cms-upload-folder" />
          </div>
          <label className="flex items-center gap-3 text-sm text-cream2 sm:col-span-2">
            <input type="checkbox" checked={uploadForm.visible} onChange={(e) => setUploadForm((f) => ({ ...f, visible: e.target.checked }))} className="accent-[#C86D3B] w-4 h-4" data-testid="cms-upload-visible" />
            Visibile sul sito pubblico
          </label>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} className="btn-copper disabled:opacity-50" data-testid="cms-upload-submit"><Upload className="h-4 w-4" /> {busy ? `Carico… ${progress}%` : "Carica in libreria"}</button>
            {busy && (
              <div className="mt-3" data-testid="cms-upload-progress">
                <div className="h-1.5 bg-obsidian2 border border-white/10">
                  <div className="h-full bg-copper transition-all duration-200" style={{ width: `${progress}%` }} data-testid="cms-upload-progress-bar" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-sand mt-1.5">Caricamento in corso · {progress}%</p>
              </div>
            )}
          </div>
        </form>
      ) : (
        <form onSubmit={submitLink} className="card-dark p-6 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="cms-link-form">
          <div className="sm:col-span-2"><label className="label">URL (YouTube, Vimeo, mp3/mp4, Spotify)</label><input className="field" required value={linkForm.url} onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://…" data-testid="cms-link-url" /></div>
          <div><label className="label">Titolo *</label><input className="field" required value={linkForm.title} onChange={(e) => setLinkForm((f) => ({ ...f, title: e.target.value }))} data-testid="cms-link-title" /></div>
          <div>
            <label className="label">Tipo</label>
            <select className="field" value={linkForm.media_type} onChange={(e) => setLinkForm((f) => ({ ...f, media_type: e.target.value }))} data-testid="cms-link-type">
              <option value="video">Video</option><option value="audio">Audio</option><option value="image">Immagine</option>
            </select>
          </div>
          <div><label className="label">Descrizione</label><input className="field" value={linkForm.description} onChange={(e) => setLinkForm((f) => ({ ...f, description: e.target.value }))} data-testid="cms-link-desc" /></div>
          <div><label className="label">Cartella (facoltativa)</label><input className="field" list="cms-folder-suggestions" placeholder="Es. Spedizione — Brasile 2027" value={linkForm.folder} onChange={(e) => setLinkForm((f) => ({ ...f, folder: e.target.value }))} data-testid="cms-link-folder" /></div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} className="btn-copper disabled:opacity-50" data-testid="cms-link-submit"><Link2 className="h-4 w-4" /> {busy ? "Aggiungo…" : "Aggiungi alla libreria"}</button>
          </div>
        </form>
      )}

      <datalist id="cms-folder-suggestions">
        {suggestedFolders.map((f) => <option key={f} value={f} />)}
      </datalist>

      <h2 className="font-serif text-2xl mt-12 mb-5">Libreria <span className="font-mono text-xs text-sand">({media.length})</span></h2>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cms-folders">
        <Folder className="h-4 w-4 text-copper" />
        <button onClick={() => setFolderFilter("")} className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (!folderFilter ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid="cms-folder-all">Tutti i file ({media.length})</button>
        {folders.map((f) => (
          <button key={f} onClick={() => setFolderFilter(f)} className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (folderFilter === f ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid={`cms-folder-${f}`}>
            {f} ({media.filter((m) => m.folder === f).length})
          </button>
        ))}
        <button onClick={() => setFolderFilter(NO_FOLDER)} className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (folderFilter === NO_FOLDER ? "border-copper text-copperlight" : "border-white/10 text-sand")} data-testid="cms-folder-none">Senza cartella ({media.filter((m) => !m.folder).length})</button>
      </div>
      {folderFilter && folderFilter !== NO_FOLDER && <p className="font-mono text-[10px] uppercase tracking-widest text-sand mb-4" data-testid="cms-folder-current">Cartella: {folderFilter} · Foto / Video / Audio</p>}

      <div className="flex flex-wrap gap-2" data-testid="cms-type-tabs">
        {TYPE_TABS.map((t) => (
          <button key={t.value} onClick={() => setTypeTab(t.value)} className={"font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border transition-colors " + (typeTab === t.value ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid={`cms-type-tab-${t.value}`}>
            <t.icon className="h-3.5 w-3.5 inline mr-2" /> {t.label} ({count(t.value)})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-6" data-testid="cms-media-grid">
        {shown.map((m) => {
          const tName = targetName(m);
          return (
            <div key={m.id} className="card-dark" data-testid={`cms-media-item-${m.id}`}>
              <Thumb m={m} />
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-serif text-lg text-cream truncate">{m.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-sand">{m.source === "upload" ? "caricato" : "link"} · {m.plays} riproduzioni</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => toggleVisible(m)} className="p-2 text-cream2 hover:text-copper" title="Visibilità" data-testid={`cms-visibility-${m.id}`}>
                      {m.visible ? <Eye className="h-4 w-4 text-copper" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button onClick={() => remove(m)} className="p-2 text-cream2 hover:text-red-400" title="Elimina" data-testid={`cms-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-cream2" data-testid={`cms-item-placement-${m.id}`}>
                  <MapPin className="h-3.5 w-3.5 text-copper shrink-0" />
                  <span className="truncate">{sectionLabel(m.placement)}{tName ? ` · ${tName}` : ""}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-cream2">
                  <Folder className="h-3.5 w-3.5 text-copper shrink-0" />
                  <select className="field !py-1.5 !text-xs flex-1" value={m.folder || ""} onChange={(e) => (e.target.value === "__new__" ? newFolder(m) : setFolder(m, e.target.value))} data-testid={`cms-item-folder-${m.id}`}>
                    <option value="">Senza cartella</option>
                    {suggestedFolders.map((f) => <option key={f} value={f}>{f}</option>)}
                    <option value="__new__">＋ Nuova cartella…</option>
                  </select>
                  <button onClick={() => newFolder(m)} className="p-1.5 text-cream2 hover:text-copper" title="Nuova cartella" data-testid={`cms-item-newfolder-${m.id}`}><FolderPlus className="h-4 w-4" /></button>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setWiz({ item: m })} className="btn-ghost flex-1 !px-4 !py-2.5 !text-xs" data-testid={`cms-destina-${m.id}`}>
                    <MapPin className="h-4 w-4" /> Destina nel sito
                  </button>
                  {m.placement && siblings(m).length > 1 && (
                    <>
                      <button onClick={() => moveItem(m, "up")} className="btn-ghost !px-3 !py-2.5" title="Sposta prima" data-testid={`cms-move-up-${m.id}`}><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => moveItem(m, "down")} className="btn-ghost !px-3 !py-2.5" title="Sposta dopo" data-testid={`cms-move-down-${m.id}`}><ArrowDown className="h-4 w-4" /></button>
                    </>
                  )}
                </div>
                {m.media_type === "audio" && (
                  <audio controls preload="none" src={resolveUrl(m.url)} onPlay={() => api.post(`/media/${m.id}/play`).catch(() => {})} className="w-full h-9 mt-3" data-testid={`cms-media-play-${m.id}`} />
                )}
              </div>
            </div>
          );
        })}
        {shown.length === 0 && <p className="text-sand text-sm col-span-full" data-testid="cms-media-empty">{EMPTY_MSG[typeTab]}</p>}
      </div>

      {wiz && <DestinaWizard item={wiz.item} tours={tours} leaders={leaders} onClose={() => setWiz(null)} onApply={applyWiz} />}
    </div>
  );
}
